import { Component, inject, DestroyRef, input, computed, effect, output, signal } from '@angular/core';
import { FormBuilder, FormControl, FormRecord, Validators } from '@angular/forms';
import {
  AddendumOptionsPayload,
  DealOption,
  DealOptionProduct
} from 'libs/sales/src/lib/models/deal-options-view.models';
import { ModelElevationProductsService } from 'libs/sales/src/lib/services/model-elevation-products.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BtnLoaderDirective, rxResourceFixed } from '@zayan-erp/shared';
import { DropdownData } from 'libs/shared/src/lib/models/dropdown.models';
import { ProductAttributeGroup, ProductLocationAssociation } from 'libs/sales/src/lib/models/deal-options.models';
import { GenericDropdownComponent } from 'libs/shared/src/lib/components/generic-dropdown/generic-dropdown.component';
import { CurrencyInputFieldComponent } from '@zayan-erp/shared';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EMPTY, catchError, filter, of, switchMap } from 'rxjs';
import { DealOptionsService } from 'libs/sales/src/lib/services/deal-options.service';
import { AbilityService } from '@zayan-erp/shared';

@Component({
  selector: 'deal-addendum-option-basic-details-form',
  imports: [
    GenericDropdownComponent,
    CurrencyInputFieldComponent,
    FormsModule,
    ReactiveFormsModule,
    BtnLoaderDirective
  ],
  templateUrl: './deal-addendum-option-basic-details-form.component.html'
})
export class DealAddendumOptionBasicDetailsFormComponent {
  readonly fb = inject(FormBuilder);
  readonly destroyRef = inject(DestroyRef);
  readonly modelElevationProductsService = inject(ModelElevationProductsService);
  readonly dealOptionsService = inject(DealOptionsService);
  readonly abilityService = inject(AbilityService);
  readonly dealAddendum = input.required<DealOption>();
  readonly addendumOption = input.required<DealOptionProduct>();
  readonly isLocked = computed(() => this.dealAddendum().isLocked || this.dealAddendum().isCancelled);
  /** Single source of truth for this component's write gate. */
  readonly canWriteDeals = computed(() => this.abilityService.ability.can('write-sales-deals', 'deals'));
  /** On a locked addendum an Admin or SuperAdmin may still move the option. Nothing else on it changes. */
  readonly canMoveLockedOption = computed(
    () =>
      this.dealAddendum().isLocked &&
      !this.dealAddendum().isCancelled &&
      !this.addendumOption().isCancelled &&
      this.canWriteDeals() &&
      this.abilityService.ability.can('edit-locked-addendum-location', 'deals')
  );

  onChanges = output<string>();

  productId = computed(() => this.addendumOption().productId!);
  readonly usingDefaultLocations = signal(false);

  readonly productLocationsResource = rxResourceFixed({
    params: () => ({ productId: this.productId()!, locked: this.dealAddendum().isLocked }),
    defaultValue: [] as ProductLocationAssociation[],
    stream: ({ params }) => {
      if (!params.productId) return of<ProductLocationAssociation[]>([]);
      return this.modelElevationProductsService.getProductLocations(this.dealAddendum().id, params.productId).pipe(
        switchMap(locations => {
          // A locked option may only move to a room this home offers for it, as when it was added.
          if (locations.length === 0 && !params.locked) {
            this.usingDefaultLocations.set(true);
            return this.modelElevationProductsService.getProductLocationsByProductId(params.productId!);
          }
          this.usingDefaultLocations.set(false);
          return of(locations);
        })
      );
    }
  });

  readonly productAttributesResource = rxResourceFixed({
    params: () => ({ productId: this.productId()! }),
    defaultValue: [] as ProductAttributeGroup[],
    stream: ({ params }) => {
      if (!params.productId) return of<ProductAttributeGroup[]>([]);
      return this.modelElevationProductsService.getProductAttributes(params.productId);
    }
  });

  readonly locationOptions = computed<DropdownData[]>(() =>
    (this.productLocationsResource.value() ?? []).map(location => ({
      id: location.id,
      firstLabel: location.locationCode,
      secondLabel: location.locationName
    }))
  );

  productAttributes = computed(() => this.productAttributesResource.value() ?? []);

  private readonly selectedAttributeValueIds = computed(
    () =>
      new Map(
        (this.addendumOption().attributes ?? []).map(attr => [
          attr.subCategoryAttributeId,
          attr.subCategoryAttributeValues?.[0]?.subCategoryAttributeValueId ?? null
        ])
      )
  );

  readonly attributeFields = computed(() => {
    const selectedIds = this.selectedAttributeValueIds();
    const attributes = this.productAttributes();
    if (!attributes?.length) {
      return [];
    }

    return attributes.map(attribute => {
      const values = attribute.productAttributeValues;
      const selectedValueId = selectedIds.get(attribute.subCategoryAttributeId) ?? null;

      return {
        id: attribute.subCategoryAttributeId,
        name: attribute.subCategoryAttributeName,
        selectedValueId,
        hasValues: values.length > 0,
        options: values
          .filter(value => value.isActive || value.id === selectedValueId)
          .map(value => ({
            id: value.id,
            firstLabel: value.value,
            secondLabel: value.isActive ? null : 'Inactive'
          }))
      };
    });
  });

  attributeValues = new FormRecord<FormControl<string | null>>({});

  productForm = this.fb.group({
    locationId: [null as string | null],
    unitPrice: [null as number | null, [Validators.required]],
    notes: [''],
    attributeValues: this.attributeValues
  });

  get attributeValuesControl(): FormRecord<FormControl<string | null>> {
    return this.attributeValues;
  }

  onDealAddendumOptionDetailsChanges = effect(() => {
    this.productForm.patchValue({
      locationId: this.addendumOption().selectedLocationId,
      unitPrice: this.addendumOption().unitPrice,
      notes: this.addendumOption().notes
    });

    const fields = this.attributeFields();
    const attributeValuesGroup = this.attributeValues;
    const currentKeys = new Set(Object.keys(attributeValuesGroup.controls));
    const newKeys = new Set(fields.map(field => field.id));

    currentKeys.forEach(key => {
      if (!newKeys.has(key)) {
        attributeValuesGroup.removeControl(key);
      }
    });

    fields.forEach(field => {
      if (!attributeValuesGroup.controls[field.id]) {
        attributeValuesGroup.addControl(field.id, this.fb.control<string | null>(field.selectedValueId));
      } else {
        attributeValuesGroup.controls[field.id].setValue(field.selectedValueId, { emitEvent: false });
      }
    });

    if (this.isLocked() || !this.canWriteDeals()) {
      this.productForm.disable({ emitEvent: false });
      if (this.canMoveLockedOption()) {
        this.productForm.controls.locationId.enable({ emitEvent: false });
      }
    } else {
      this.productForm.enable({ emitEvent: false });
    }
  });

  constructor() {
    // A locked option's new location is saved on its own, without the rest of the form or a re-price.
    this.productForm.controls.locationId.valueChanges
      .pipe(
        filter(locationId => this.canMoveLockedOption() && locationId !== this.addendumOption().selectedLocationId),
        switchMap(locationId =>
          this.dealOptionsService.updateAddendumOptionLocation(this.addendumOption().id, locationId).pipe(
            catchError(() => {
              this.productForm.controls.locationId.setValue(this.addendumOption().selectedLocationId, {
                emitEvent: false
              });
              return EMPTY;
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.onChanges.emit(this.addendumOption().id));
  }

  // Get form data in API payload format
  getFormData(): AddendumOptionsPayload {
    const formValue = this.productForm.getRawValue();

    // Convert attributeValues object to array format for API
    const attributes = Object.entries(formValue.attributeValues || {})
      .filter(([_, valueId]) => valueId !== null)
      .map(([attributeId, valueId]) => ({
        subCategoryAttributeId: attributeId,
        subCategoryAttributeValueId: valueId as string
      }));

    const payload: AddendumOptionsPayload = {
      modelElevationProductsId: this.addendumOption().modelElevationProductsId,
      selectedLocationId: formValue.locationId as string,
      unitPrice: formValue.unitPrice ?? 0,
      notes: formValue.notes || '',
      attributes
    };
    return payload;
  }

  // Check if form is valid
  isFormValid(): boolean {
    return this.productForm.valid;
  }

  onSubmit(): void {
    if (this.isLocked() || !this.canWriteDeals()) {
      return;
    }

    if (this.productForm.invalid) {
      this.productForm.markAllAsTouched();
      return;
    }
    const payload = this.getFormData();

    this.dealOptionsService
      .updateAddendumOption(this.addendumOption().id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.onChanges.emit(this.addendumOption().id);
        }
      });
  }
}
